import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Search, Utensils } from "lucide-react";
import { PlanConfigForm } from "@/components/mealplan/PlanConfigForm";
import { AIGenerationLoader } from "@/components/mealplan/AIGenerationLoader";
import { PatientSummaryWrapper } from "@/components/mealplan/PatientSummaryWrapper";
import { toast } from "sonner";

export default function MealPlanCreate() {
  const navigate = useNavigate();
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: patients, isLoading } = useQuery({
    queryKey: ["patients-for-meal-plan"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Não autenticado");

      const { data, error } = await supabase
        .from("patients_onboarding")
        .select("*")
        .eq("owner_id", user.id)
        .eq("status", "active")
        .order("full_name");

      if (error) throw error;
      return data;
    },
  });

  const filteredPatients = patients?.filter((p) =>
    p.full_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleGeneratePlan = async (config: any) => {
    if (!selectedPatientId) {
      toast.error("Selecione um paciente");
      return;
    }

    setIsGenerating(true);

    try {
      const { data, error } = await supabase.functions.invoke("generate-meal-plan", {
        body: {
          patientId: selectedPatientId,
          meals: config.meals,
          objective: config.objective,
          customObjective: config.customObjective,
          notes: config.notes,
          daysCount: config.daysCount,
        },
      });

      if (error) throw error;

      toast.success("Plano alimentar gerado com sucesso!");
      navigate(`/app/aplicativo/mealplan/${data.planId}`);
    } catch (error: any) {
      console.error("Error generating meal plan:", error);
      toast.error(error.message || "Erro ao gerar plano alimentar");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/app/aplicativo/mealplan")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Utensils className="h-8 w-8 text-green-600" />
            Criar Plano Alimentar
          </h1>
          <p className="text-muted-foreground">
            Selecione um paciente e configure o plano alimentar
          </p>
        </div>
      </div>

      {!selectedPatientId ? (
        <Card>
          <CardHeader>
            <CardTitle>Selecione um Paciente</CardTitle>
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar paciente..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                Carregando pacientes...
              </div>
            ) : !filteredPatients || filteredPatients.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum paciente encontrado
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredPatients.map((patient) => (
                  <Card
                    key={patient.id}
                    className="cursor-pointer hover:bg-accent transition-colors"
                    onClick={() => setSelectedPatientId(patient.id)}
                  >
                    <CardContent className="flex items-center gap-4 p-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={patient.avatar_url || ""} />
                        <AvatarFallback>
                          {patient.full_name.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold">{patient.full_name}</p>
                        {patient.email && (
                          <p className="text-sm text-muted-foreground">{patient.email}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => setSelectedPatientId(null)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Trocar Paciente
            </Button>
          </div>

          <PatientSummaryWrapper patientId={selectedPatientId} />

          {isGenerating ? (
            <AIGenerationLoader />
          ) : (
            <PlanConfigForm onSubmit={handleGeneratePlan} loading={isGenerating} />
          )}
        </div>
      )}
    </div>
  );
}
